import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    IconButton,
    Radio,
    RadioGroup,
    FormControlLabel,
    TextField,
    Button,
    CircularProgress
} from '@mui/material';
import { CloseOutlinedIcon } from 'icons/materialIcons';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import Modal from 'components/common/Modal';
import TeamDarkButtonWithIcon from 'team/shared/components/button/TeamDarkButtonWithIcon';
import { duplicateTeamMember } from 'team/apis/teamApi';

const TeamDuplicateMemberModal = ({ open, onClose, memberData }) => {
    const navigate = useNavigate();
    const [cardSelection, setCardSelection] = useState('default'); // 'all' or 'default'
    const [duplicateCount, setDuplicateCount] = useState(1);
    const [loading, setLoading] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const handleDuplicate = () => {
        // Show confirmation modal first
        setShowConfirmation(true);
    };

    const handleConfirm = async () => {
        setShowConfirmation(false); // Close confirmation modal

        try {
            setLoading(true);
            console.log('[TeamDuplicateMemberModal] Duplicating member:', {
                userId: memberData.userId,
                teamId: memberData.teamId,
                cardSelection,
                count: duplicateCount
            });

            const response = await duplicateTeamMember(memberData.teamId, memberData.userId, {
                count: duplicateCount,
                includeAllCards: cardSelection === 'all'
            });

            if (response.success) {
                setSnackbar({
                    open: true,
                    message: `Successfully created ${duplicateCount} duplicate member(s)`,
                    severity: 'success'
                });

                // Close modal and navigate to members list after short delay
                setTimeout(() => {
                    onClose();
                    navigate('/team/members');
                }, 1500);
            }
        } catch (error) {
            console.error('[TeamDuplicateMemberModal] Error duplicating member:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to duplicate member',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCancelConfirmation = () => {
        setShowConfirmation(false);
    };

    const handleNotificationClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    return (
        <>
            <Modal
                open={open}
                onClose={onClose}
                width={{ xs: '90%', sm: '70%', md: '45%' }}
                height="auto"
            >
                <Box sx={{ padding: 0, position: 'relative' }}>
                    {/* Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: { xs: 2, sm: 3 },
                            borderBottom: '1px solid #e0e0e0'
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                fontSize: { xs: '1rem', sm: '1.2rem' },
                                color: '#333'
                            }}
                        >
                            Duplicate Team Member
                        </Typography>
                        <IconButton
                            onClick={onClose}
                            sx={{
                                color: '#828282',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5'
                                }
                            }}
                        >
                            <CloseOutlinedIcon />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <Box sx={{ padding: { xs: 2, sm: 3 } }}>
                        {/* Description */}
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                color: '#666',
                                marginBottom: 3,
                                lineHeight: 1.6
                            }}
                        >
                            Select which cards from this member to include in the duplicates and enter the number of duplicates you'd like to create. Choose profile info to duplicate.
                        </Typography>

                        {/* Card Selection */}
                        <Box sx={{ marginBottom: 3 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    fontWeight: 600,
                                    color: '#4f4f4f',
                                    marginBottom: 2
                                }}
                            >
                                Select Cards to Duplicate
                            </Typography>
                            <RadioGroup
                                value={cardSelection}
                                onChange={(e) => setCardSelection(e.target.value)}
                                sx={{ gap: 1 }}
                            >
                                <FormControlLabel
                                    value="default"
                                    control={<Radio size="small" />}
                                    label={
                                        <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            Default card
                                        </Typography>
                                    }
                                />
                                <FormControlLabel
                                    value="all"
                                    control={<Radio size="small" />}
                                    label={
                                        <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            All cards
                                        </Typography>
                                    }
                                />
                            </RadioGroup>
                        </Box>

                        {/* Number of Duplicates */}
                        <Box sx={{ marginBottom: 3 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    fontWeight: 600,
                                    color: '#4f4f4f',
                                    marginBottom: 1
                                }}
                            >
                                Number of Duplicates
                            </Typography>
                            <TextField
                                type="number"
                                value={duplicateCount}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '') {
                                        setDuplicateCount('');
                                    } else {
                                        const num = parseInt(value);
                                        if (!isNaN(num) && num >= 1 && num <= 50) {
                                            setDuplicateCount(num);
                                        }
                                    }
                                }}
                                inputProps={{ min: 1, max: 50 }}
                                sx={{
                                    width: '100%',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px'
                                    }
                                }}
                            />
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '0.7rem',
                                    color: '#999',
                                    marginTop: 0.5,
                                    display: 'block'
                                }}
                            >
                                Enter a number between 1 and 50
                            </Typography>
                        </Box>

                        {/* Buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, marginTop: 3 }}>
                            <Button
                                variant="outlined"
                                onClick={onClose}
                                sx={{
                                    borderColor: '#e0e0e0',
                                    color: '#828282',
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    padding: '8px 24px',
                                    '&:hover': {
                                        borderColor: '#bdbdbd',
                                        backgroundColor: 'transparent'
                                    }
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleDuplicate}
                                disabled={loading || !duplicateCount || duplicateCount < 1}
                                sx={{
                                    backgroundColor: '#000',
                                    color: '#fff',
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    padding: '8px 24px',
                                    minWidth: '120px',
                                    '&:hover': {
                                        backgroundColor: '#333'
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#e0e0e0',
                                        color: '#bdbdbd'
                                    }
                                }}
                            >
                                Continue
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            {/* Confirmation Modal */}
            <Modal
                open={showConfirmation}
                onClose={handleCancelConfirmation}
                width={{ xs: '90%', sm: '400px', md: '400px' }}
                height="auto"
            >
                <Box sx={{ padding: 0, position: 'relative' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: { xs: 2, sm: 2.5 },
                            borderBottom: '1px solid #e0e0e0'
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                                color: '#333'
                            }}
                        >
                            Create {duplicateCount} duplicate member{duplicateCount > 1 ? 's' : ''}
                        </Typography>
                        <IconButton
                            onClick={handleCancelConfirmation}
                            sx={{
                                color: '#828282',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5'
                                }
                            }}
                        >
                            <CloseOutlinedIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ padding: { xs: 2, sm: 2.5 } }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                color: '#666',
                                marginBottom: 3,
                                lineHeight: 1.6
                            }}
                        >
                            You are about to create {duplicateCount} member{duplicateCount > 1 ? 's' : ''}. This will add to your monthly payment.
                        </Typography>

                        {/* Buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={handleCancelConfirmation}
                                sx={{
                                    borderColor: '#e0e0e0',
                                    color: '#828282',
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    padding: '8px 24px',
                                    '&:hover': {
                                        borderColor: '#bdbdbd',
                                        backgroundColor: 'transparent'
                                    }
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleConfirm}
                                disabled={loading}
                                sx={{
                                    backgroundColor: '#000',
                                    color: '#fff',
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    padding: '8px 24px',
                                    minWidth: '100px',
                                    '&:hover': {
                                        backgroundColor: '#333'
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#e0e0e0',
                                        color: '#bdbdbd'
                                    }
                                }}
                            >
                                {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Duplicate'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            {/* Notification Snackbar */}
            <TeamNotificationSnackbar
                open={snackbar.open}
                message={snackbar.message}
                handleClose={handleNotificationClose}
                severity={snackbar.severity}
            />
        </>
    );
};

export default TeamDuplicateMemberModal;

